/**
 * ProposalReviewPanel — the "Proposals to review" band at the top of the
 * submission Issues tab.
 *
 * When a bulk-resolve pass has produced pending proposals, this band steps the
 * writer through them one at a time (prev / next), showing each as a before/after
 * diff with Approve / Reject / Revise and scrolling the open Word document to the
 * target paragraph as each card becomes active. Renders nothing when the queue is
 * empty. All logic lives in ``useProposalReviewFlow``; this is the wiring.
 */

import { makeStyles, Spinner } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors, surfaceColors } from "../tokens";
import { useProposalReviewFlow } from "../hooks/useProposalReviewFlow";
import { ProposalReviewCard } from "./ProposalReviewCard";

interface ProposalReviewPanelProps {
  runId: string | null;
  /** Bumped by the shell when a resolve pass produces a proposal / finishes. */
  refreshSignal?: number;
  /** Active source library — scopes a Revise rewrite's retrieval. */
  libraryId?: string | null;
  /** Called after each approve/reject so the host refreshes the Issues list. */
  onDecided?: () => void;
  /** Surface a writer-readable error (rendered inline by the host, or here). */
  onError?: (message: string) => void;
}

const useStyles = makeStyles({
  root: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    background: surfaceColors.paperDim,
    padding: "10px 12px",
    marginBottom: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink,
  },
  count: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink3,
    fontVariantNumeric: "tabular-nums",
  },
  spacer: { flex: 1 },
  navBtn: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: surfaceColors.paper,
    borderRadius: "4px",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    padding: "3px 9px",
    ":disabled": { opacity: 0.45, cursor: "default" },
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink4,
  },
  error: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: "#B71C1C",
  },
});

export const ProposalReviewPanel: React.FC<ProposalReviewPanelProps> = ({
  runId,
  refreshSignal,
  libraryId,
  onDecided,
  onError,
}) => {
  const styles = useStyles();
  const flow = useProposalReviewFlow({ runId, refreshSignal, libraryId, onDecided, onError });

  // Nothing to review and not mid-load → render nothing (the band only appears
  // when a pass has produced proposals).
  if (flow.queue.length === 0) {
    if (flow.phase === "loading") {
      return (
        <div className={styles.root}>
          <div className={styles.loading}>
            <Spinner size="extra-tiny" />
            <span>Loading proposed changes…</span>
          </div>
        </div>
      );
    }
    return null;
  }

  const active = flow.active;
  const busy = flow.phase === "applying";
  const revising = flow.phase === "revising";

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <span className={styles.title}>Proposals to review</span>
        <span className={styles.count}>
          {flow.activeIndex + 1} / {flow.queue.length}
        </span>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.navBtn}
          onClick={flow.prev}
          disabled={flow.activeIndex === 0 || busy || revising}
        >
          ‹ Prev
        </button>
        <button
          type="button"
          className={styles.navBtn}
          onClick={flow.next}
          disabled={flow.activeIndex >= flow.queue.length - 1 || busy || revising}
        >
          Next ›
        </button>
      </div>

      {flow.error ? <div className={styles.error}>{flow.error}</div> : null}

      {active ? (
        <ProposalReviewCard
          key={active.proposal.id}
          proposal={active.proposal}
          sectionNumber={active.sectionNumber}
          sectionTitle={active.sectionTitle}
          busy={busy}
          revising={revising}
          progressLabel={flow.progressLabel}
          onApprove={() => void flow.approve()}
          onReject={() => void flow.reject()}
          onRevise={flow.revise}
        />
      ) : null}
    </div>
  );
};

export default ProposalReviewPanel;
